import { fetcher } from '@/dashboard/services/request';

class FontApiService {
  constructor() {
    this.cache = {
      fonts: null,
      categories: null,
      lastFetch: null,
      cacheExpiryMs: 30 * 60 * 1000, // 30 minutes
    };
  }

  /**
   * Check if cached data is still valid
   */
  isCacheValid() {
    if (!this.cache.lastFetch) return false;
    return Date.now() - this.cache.lastFetch < this.cache.cacheExpiryMs;
  }

  /**
   * Get all available fonts
   * @param {Object} options - Query options
   * @param {string} options.search - Search query for font names
   * @param {string} options.category - Filter by category
   * @param {boolean} options.popular - Get popular fonts only
   * @param {number} options.limit - Limit number of results
   * @param {number} options.offset - Offset for pagination
   * @param {boolean} options.useCache - Whether to use cached data (default: true)
   * @returns {Promise<Array>} Array of font objects
   */
  async getFonts(options = {}) {
    const { search, category, popular, limit = 20, offset = 0, useCache = true } = options;

    // Return cached data if valid and no specific filters
    if (
      useCache &&
      this.isCacheValid() &&
      this.cache.fonts &&
      !search &&
      !category &&
      !popular &&
      offset === 0
    ) {
      return this.cache.fonts.slice(offset, offset + limit);
    }

    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (popular) params.append('popular', 'true');
      if (limit !== 20) params.append('limit', limit);
      if (offset > 0) params.append('offset', offset);

      const queryString = params.toString();
      const url = `/fonts${queryString ? `?${queryString}` : ''}`;
      const response = await fetcher(url, null, { method: 'GET' });

      if (!response || !response.fonts) {
        throw new Error('Invalid response format from fonts API');
      }

      const fonts = response.fonts;

      // Cache the full font list if no filters were applied
      if (!search && !category && !popular) {
        this.cache.fonts = fonts;
        this.cache.lastFetch = Date.now();
      }

      return fonts;
    } catch (error) {
      console.error('Error fetching fonts:', error);

      // Return cached data as fallback if available
      if (this.cache.fonts) {
        return this.cache.fonts.slice(0, limit);
      }

      // Return fallback list if no cache available
      return this.getFallbackFonts();
    }
  }

  /**
   * Get a specific font by family name
   * @param {string} familyName - Font family name
   * @returns {Promise<Object|null>} Font object or null if not found
   */
  async getFontByFamily(familyName) {
    if (!familyName) return null;

    try {
      const encodedName = encodeURIComponent(familyName);
      const response = await fetcher(`/fonts/${encodedName}`, null, { method: 'GET' });

      if (!response || !response.font) {
        return null;
      }

      return response.font;
    } catch (error) {
      console.error(`Error fetching font '${familyName}':`, error);
      return null;
    }
  }

  /**
   * Get available weights for a specific font
   * @param {string} familyName - Font family name
   * @returns {Promise<Array>} Array of available weights
   */
  async getFontWeights(familyName) {
    if (!familyName) return [400];

    try {
      const encodedName = encodeURIComponent(familyName);
      const response = await fetcher(`/fonts/${encodedName}/weights`, null, { method: 'GET' });

      if (!response || !response.weights) {
        return [400];
      }

      return response.weights;
    } catch (error) {
      console.error(`Error fetching weights for font '${familyName}':`, error);
      return [400];
    }
  }

  /**
   * Get all available font categories
   * @param {boolean} useCache - Whether to use cached data (default: true)
   * @returns {Promise<Array>} Array of category strings
   */
  async getCategories(useCache = true) {
    // Return cached data if valid
    if (useCache && this.isCacheValid() && this.cache.categories) {
      return this.cache.categories;
    }

    try {
      const response = await fetcher('/fonts/categories', null, { method: 'GET' });

      if (!response || !response.categories) {
        throw new Error('Invalid response format from categories API');
      }

      const categories = response.categories;
      this.cache.categories = categories;

      return categories;
    } catch (error) {
      console.error('Error fetching font categories:', error);

      // Return cached data as fallback
      if (this.cache.categories) {
        return this.cache.categories;
      }

      // Return default categories
      return ['serif', 'sans-serif', 'display', 'handwriting', 'monospace'];
    }
  }

  /**
   * Search fonts by name
   * @param {string} query - Search query
   * @param {number} limit - Maximum number of results
   * @returns {Promise<Array>} Array of matching font objects
   */
  async searchFonts(query, limit = 50) {
    if (!query || !query.trim()) {
      return this.getFonts({ limit });
    }

    return this.getFonts({ search: query.trim(), limit, useCache: false });
  }

  /**
   * Get popular fonts
   * @param {number} limit - Maximum number of results
   * @returns {Promise<Array>} Array of popular font objects
   */
  async getPopularFonts(limit = 100) {
    return this.getFonts({ popular: true, limit, useCache: false });
  }

  /**
   * Refresh the font cache
   * @returns {Promise<Array>} Fresh font data
   */
  async refreshCache() {
    this.cache.fonts = null;
    this.cache.categories = null;
    this.cache.lastFetch = null;

    return this.getFonts({ useCache: false });
  }

  /**
   * Get cache information
   * @returns {Object} Cache statistics
   */
  getCacheInfo() {
    return {
      hasCachedFonts: !!this.cache.fonts,
      hasCachedCategories: !!this.cache.categories,
      lastFetch: this.cache.lastFetch,
      cacheAge: this.cache.lastFetch ? Date.now() - this.cache.lastFetch : null,
      isValid: this.isCacheValid(),
      fontCount: this.cache.fonts ? this.cache.fonts.length : 0,
    };
  }

  /**
   * Fallback font list for when API is unavailable
   * This matches the structure expected by the FontCombobox
   * @returns {Array} Basic font list
   */
  getFallbackFonts() {
    return [
      {
        family: 'Inter',
        category: 'sans-serif',
        weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
      },
      { family: 'Roboto', category: 'sans-serif', weights: [100, 300, 400, 500, 700, 900] },
      { family: 'Open Sans', category: 'sans-serif', weights: [300, 400, 500, 600, 700, 800] },
      { family: 'Lato', category: 'sans-serif', weights: [100, 300, 400, 700, 900] },
      {
        family: 'Montserrat',
        category: 'sans-serif',
        weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
      },
      {
        family: 'Source Sans 3',
        category: 'sans-serif',
        weights: [200, 300, 400, 500, 600, 700, 800, 900],
      },
      { family: 'Playfair Display', category: 'serif', weights: [400, 500, 600, 700, 800, 900] },
      { family: 'Merriweather', category: 'serif', weights: [300, 400, 700, 900] },
      {
        family: 'Poppins',
        category: 'sans-serif',
        weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
      },
      {
        family: 'Nunito',
        category: 'sans-serif',
        weights: [200, 300, 400, 500, 600, 700, 800, 900],
      },
    ];
  }
}

// Export singleton instance
export default new FontApiService();
